/**
 * Mattress Quiz
 *
 * Drives the multi-step quiz in sections/mattress-quiz.liquid and scores the product list
 * embedded in that section's `[data-quiz-products]` JSON island (built server-side from
 * product metafields — see the Liquid file for exactly what each field means).
 *
 * Scoring approach: firmness ("feel") is the one metafield here that's a validated, fixed-choice
 * list (custom.mattress_feel1), so it's the primary signal — sleep position, weight and reported
 * sleep issues each nudge a 1-7 target firmness (1 = Ultra-Firm … 7 = Ultra-Soft, same scale as
 * sections/mattress-specs.liquid) using standard mattress-industry guidance (side sleepers want
 * softer pressure relief at the shoulder/hip, stomach sleepers want firmer support to stop the
 * hips sinking, heavier bodies need firmer support to avoid excess compression, etc.). If the
 * shopper answers the direct "how would you like it to feel" question, that overrides the nudges
 * entirely since it's an explicit preference. Size is a hard filter (mattresses are single-variant
 * products here, so each size is its own product/URL). Everything else (construction, cooling,
 * certifications…) lives in free-text metafields with no fixed vocabulary, so those are matched
 * as soft scoring bonuses via keyword search rather than hard filters.
 */

const FEEL_POSITIONS = {
  'ultra-firm': 1,
  firm: 2,
  'medium-firm': 3,
  medium: 4,
  'medium-soft': 5,
  soft: 6,
  'ultra-soft': 7
};

// Buying for a child, teenager, or guest room — the personal sleep-habit questions (position,
// issues, weight) are about the answerer's own body, which doesn't apply, so the quiz jumps
// straight from "who's it for" to "what size" for these answers.
const SKIP_TO_SIZE_AUDIENCES = new Set(['child', 'teenager', 'guest']);

// OMF's product ranges, ascending in price — mirrors the "Original Low Price / Essentials /
// Modern / Family / Signature" tagging used storewide (see templates/index.json's range tiles).
// Products carry one of these as an exact tag; mattresses without one of these tags (e.g. the
// third-party Bedgear line) fall back to plain price-based ordering below.
const RANGE_ORDER = ['Original Low Price', 'Essentials', 'Modern', 'Family', 'Signature'];

const FEATURE_KEYWORDS = {
  'australian-made': ['australian made', 'made in australia'],
  'adjustable-bed': ['adjustable bed', 'adjustable base'],
  'pillow-top': ['pillow top', 'pillow-top'],
  cooling: ['cool', 'gel', 'breathab', 'charcoal', 'ventilat'],
  spring: ['spring'],
  foam: ['foam'],
  hybrid: ['hybrid'],
  'partner-disturbance': ['pocket spring', 'motion isolation', 'hybrid', 'zoned support']
};

function blobMatches(blob, keywords) {
  return keywords.some((keyword) => blob.includes(keyword));
}

function computeTargetFeel(answers) {
  let target = 4; // Medium baseline

  switch (answers.position) {
    case 'side':
      target += 1.5;
      break;
    case 'back':
      target -= 0.5;
      break;
    case 'stomach':
      target -= 1.5;
      break;
  }

  switch (answers.weight) {
    case 'under-70':
      target += 1;
      break;
    case '100-120':
      target -= 1;
      break;
    case 'over-120':
      target -= 2;
      break;
  }

  if (answers.audience === 'child' || answers.audience === 'teenager') {
    target -= 0.3;
  } else if (answers.audience === 'guest') {
    target = target * 0.7 + 4 * 0.3;
  }

  const issues = answers.issues || new Set();
  if (issues.has('unsupported') || issues.has('aches-pains')) {
    target -= 1;
  }

  if (answers.feel && answers.feel !== 'not-sure' && FEEL_POSITIONS[answers.feel]) {
    target = FEEL_POSITIONS[answers.feel];
  }

  return Math.min(7, Math.max(1, target));
}

function normalizeSize(value) {
  return (value || '').toLowerCase().replace(/[\s-]/g, '');
}

function scoreProduct(product, answers, targetFeel) {
  let score = 0;

  if (product.feelPosition > 0) {
    score -= Math.abs(product.feelPosition - targetFeel) * 2;
  }

  const issues = answers.issues || new Set();
  if ((issues.has('unsupported') || issues.has('aches-pains')) && product.hasErgonomicSupport) {
    score += 2;
  }
  if (issues.has('hot') && product.hasCooling) {
    score += 2;
  }
  if (issues.has('hot') && blobMatches(product.blob, FEATURE_KEYWORDS.cooling)) {
    score += 1;
  }
  if (issues.has('partner-movement') && blobMatches(product.blob, FEATURE_KEYWORDS['partner-disturbance'])) {
    score += 2;
  }
  if (issues.has('allergies') && blobMatches(product.blob, ['certipur', 'oeko-tex', 'hypoallergenic', 'certified'])) {
    score += 1;
  }

  const features = answers.features || new Set();
  features.forEach((feature) => {
    if (feature === 'not-sure') return;
    const keywords = FEATURE_KEYWORDS[feature];
    if (keywords && blobMatches(product.blob, keywords)) {
      score += 1.5;
    }
  });

  if (!product.available) {
    score -= 1;
  }

  return score;
}

const RESULT_TIER_LABELS = ['Budget-Friendly', 'Great Match', 'Premium Pick'];
const QUALIFIED_SCORE_MARGIN = 2; // roughly "within 1 firmness point" of the best match
const QUALIFIED_POOL_SIZE = 10;

/**
 * Sort key that spreads products by OMF's actual range tier first (Original Low Price →
 * Signature), falling back to raw price for anything untagged, since ranges ascend in price
 * by design — this keeps the spread meaningful ("one from each tier") rather than just picking
 * 3 arbitrary price points that could all sit inside the same range.
 */
function spreadRank(product) {
  const rangeIndex = RANGE_ORDER.indexOf(product.range);
  const price = product.price || 0;
  if (rangeIndex === -1) return price;
  return rangeIndex * 1e9 + price;
}

/**
 * Picks 3 well-matched products spread across OMF's price tiers (cheap/mid/premium range)
 * rather than just the 3 single best-scoring products, which tend to cluster in one range.
 * Only pulls from products that are still a good match (within QUALIFIED_SCORE_MARGIN of the top
 * score) so the spread never sacrifices fit just to hit 3 different price points — and critically,
 * *within* each tier it picks the best-SCORING product, not the cheapest/priciest one, so which
 * specific product represents a tier still depends on the quiz answers rather than only on price.
 */
function pickPriceSpread(scored) {
  if (scored.length === 0) return [];

  const topScore = scored[0].score;
  let qualified = scored.filter((entry) => entry.score >= topScore - QUALIFIED_SCORE_MARGIN);
  if (qualified.length < 3) {
    qualified = scored.slice(0, Math.min(scored.length, 3));
  }
  qualified = qualified.slice(0, QUALIFIED_POOL_SIZE);

  const buckets = new Map();
  qualified.forEach((entry) => {
    const rangeIndex = RANGE_ORDER.indexOf(entry.product.range);
    const bucketKey = rangeIndex === -1 ? 'untagged' : rangeIndex;
    if (!buckets.has(bucketKey)) buckets.set(bucketKey, []);
    buckets.get(bucketKey).push(entry);
  });

  // Best fit first within each tier; price only breaks ties between equally good matches.
  buckets.forEach((entries) => {
    entries.sort((a, b) => b.score - a.score || (a.product.price || 0) - (b.product.price || 0));
  });

  const tierKeys = [...buckets.keys()]
    .filter((key) => key !== 'untagged')
    .sort((a, b) => a - b);

  const seen = new Set();
  const takeBest = (entries) => {
    const pick = (entries || []).find((entry) => !seen.has(entry.product.handle));
    if (pick) seen.add(pick.product.handle);
    return pick;
  };

  const picks = [];

  if (tierKeys.length >= 2) {
    const low = takeBest(buckets.get(tierKeys[0]));
    const high = takeBest(buckets.get(tierKeys[tierKeys.length - 1]));
    let mid = tierKeys.length >= 3 ? takeBest(buckets.get(tierKeys[Math.floor((tierKeys.length - 1) / 2)])) : null;
    if (!mid) {
      // No distinct middle tier available — fall back to the next best individual match overall.
      mid = takeBest(qualified);
    }
    [low, mid, high].forEach((entry) => entry && picks.push(entry));
  } else {
    qualified.forEach((entry) => {
      if (picks.length >= 3 || seen.has(entry.product.handle)) return;
      seen.add(entry.product.handle);
      picks.push(entry);
    });
  }

  // Backfill from the full scored list if still short of 3 distinct products.
  scored.forEach((entry) => {
    if (picks.length >= 3 || seen.has(entry.product.handle)) return;
    seen.add(entry.product.handle);
    picks.push(entry);
  });

  return picks
    .sort((a, b) => spreadRank(a.product) - spreadRank(b.product))
    .map((entry, index) =>
      Object.assign({}, entry.product, { tier: entry.product.range || RESULT_TIER_LABELS[index] || null })
    );
}

function scoreAndSort(products, answers, targetFeel) {
  return products
    .map((product) => ({ product, score: scoreProduct(product, answers, targetFeel) }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.product.price || 0) - (b.product.price || 0);
    });
}

// Buying for a child, teenager, or guest room skips the personal sleep-habit questions (see
// SKIP_TO_SIZE_AUDIENCES in the MattressQuiz class) since those are about the answerer's own
// body, not whoever's actually using the mattress — and steers toward the more value-oriented
// ranges rather than the premium Family/Signature tiers, per the client's explicit request.
const RANGE_RESTRICTED_AUDIENCES = new Set(['child', 'teenager', 'guest']);
const RANGE_RESTRICTED_TO = new Set(['Original Low Price', 'Essentials', 'Modern']);

function getRecommendations(products, answers) {
  const targetFeel = computeTargetFeel(answers);

  let eligibleProducts = products;
  if (RANGE_RESTRICTED_AUDIENCES.has(answers.audience)) {
    const restricted = products.filter((product) => RANGE_RESTRICTED_TO.has(product.range));
    if (restricted.length > 0) {
      eligibleProducts = restricted;
    }
  }

  let sizeMatched = eligibleProducts;
  let usedSizeFallback = false;

  const selectedSize = answers.size;
  if (selectedSize && selectedSize !== 'not-sure') {
    const normalized = normalizeSize(selectedSize);
    const bySize = eligibleProducts.filter(
      (product) => product.size && normalizeSize(product.size) === normalized
    );
    if (bySize.length > 0) {
      sizeMatched = bySize;
    } else {
      usedSizeFallback = true;
    }
  }

  let picks = pickPriceSpread(scoreAndSort(sizeMatched, answers, targetFeel));

  // The selected size might only have 1-2 eligible products — rather than leave the results
  // grid half-empty, fill remaining slots with the next best matches from every size.
  if (picks.length < 3 && sizeMatched !== eligibleProducts) {
    usedSizeFallback = true;
    const seen = new Set(picks.map((product) => product.handle));
    scoreAndSort(eligibleProducts, answers, targetFeel).forEach((entry) => {
      if (picks.length >= 3 || seen.has(entry.product.handle)) return;
      seen.add(entry.product.handle);
      picks.push(entry.product);
    });
    picks = picks
      .sort((a, b) => spreadRank(a) - spreadRank(b))
      .map((product, index) => Object.assign({}, product, { tier: product.range || RESULT_TIER_LABELS[index] || null }));
  }

  return {
    results: picks,
    usedSizeFallback
  };
}

class MattressQuiz extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('[data-quiz-form]');
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.continueBtn = this.querySelector('[data-quiz-continue]');
    this.backBtn = this.querySelector('[data-quiz-back]');
    this.retakeBtn = this.querySelector('[data-quiz-retake]');
    this.progressWrap = this.querySelector('[data-quiz-progress]');
    this.resultsPanel = this.querySelector('[data-quiz-results-panel]');
    this.resultsGrid = this.querySelector('[data-quiz-results]');
    this.fallbackNote = this.querySelector('[data-quiz-fallback-note]');

    // One segment per question, built here rather than in Liquid so the count can never drift
    // out of sync with the actual number of steps. showStep() just toggles .is-filled on each.
    this.progressSegments = this.steps.map(() => {
      const segment = document.createElement('span');
      segment.className = 'mattress-quiz__progress-segment';
      this.progressWrap.appendChild(segment);
      return segment;
    });

    const dataEl = this.querySelector('[data-quiz-products]');
    this.products = dataEl ? JSON.parse(dataEl.textContent) : [];

    this.answers = {};
    this.currentIndex = 0;
    this.history = [0];

    // Questions with a lot of options (6+) go two-column on wider screens (see the
    // .mattress-quiz__options--grid media query) so the Continue button fits in the viewport
    // without scrolling — short questions stay a single column, which reads better.
    const GRID_OPTION_THRESHOLD = 5;
    this.steps.forEach((step) => {
      const options = step.querySelector('.mattress-quiz__options');
      const optionCount = step.querySelectorAll('.mattress-quiz__option').length;
      if (options && optionCount > GRID_OPTION_THRESHOLD) {
        options.classList.add('mattress-quiz__options--grid');
      }
    });

    this.onOptionChange = this.onOptionChange.bind(this);
    this.onContinue = this.onContinue.bind(this);
    this.onBack = this.onBack.bind(this);
    this.onRetake = this.onRetake.bind(this);
  }

  connectedCallback() {
    this.form.addEventListener('change', this.onOptionChange);
    this.continueBtn.addEventListener('click', this.onContinue);
    this.backBtn.addEventListener('click', this.onBack);
    this.retakeBtn.addEventListener('click', this.onRetake);

    this.progressWrap.hidden = false;
    this.showStep(0);
  }

  disconnectedCallback() {
    this.form.removeEventListener('change', this.onOptionChange);
    this.continueBtn.removeEventListener('click', this.onContinue);
    this.backBtn.removeEventListener('click', this.onBack);
    this.retakeBtn.removeEventListener('click', this.onRetake);
  }

  onOptionChange(event) {
    const input = event.target;
    if (!input.matches('.mattress-quiz__option-input')) return;

    if (input.type === 'checkbox') {
      this.handleCheckboxChange(input);
    }

    this.updateContinueState();
  }

  handleCheckboxChange(changedInput) {
    const step = changedInput.closest('[data-quiz-step]');
    const groupInputs = Array.from(step.querySelectorAll('.mattress-quiz__option-input'));

    if (changedInput.checked && changedInput.hasAttribute('data-quiz-exclusive')) {
      groupInputs.forEach((input) => {
        if (input !== changedInput) input.checked = false;
      });
    } else if (changedInput.checked) {
      groupInputs.forEach((input) => {
        if (input !== changedInput && input.hasAttribute('data-quiz-exclusive')) {
          input.checked = false;
        }
      });
    }
  }

  updateContinueState() {
    const step = this.steps[this.currentIndex];
    const hasAnswer = step.querySelectorAll('.mattress-quiz__option-input:checked').length > 0;
    this.continueBtn.disabled = !hasAnswer;
  }

  readStepAnswer(step) {
    const key = step.dataset.key;
    const type = step.dataset.type;
    const checked = Array.from(step.querySelectorAll('.mattress-quiz__option-input:checked'));

    if (type === 'multi') {
      this.answers[key] = new Set(checked.map((input) => input.value));
    } else {
      this.answers[key] = checked.length ? checked[0].value : undefined;
    }
  }

  onContinue() {
    const step = this.steps[this.currentIndex];
    this.readStepAnswer(step);

    if (this.currentIndex === this.steps.length - 1) {
      this.showResults();
      return;
    }

    const nextIndex = this.getNextStepIndex(step);
    this.history.push(nextIndex);
    this.showStep(nextIndex);
  }

  // Buying for a child/teenager/guest room skips straight to the size question — see
  // SKIP_TO_SIZE_AUDIENCES. Any other step just advances to the next one in order.
  getNextStepIndex(currentStep) {
    if (currentStep.dataset.key === 'audience' && SKIP_TO_SIZE_AUDIENCES.has(this.answers.audience)) {
      const sizeIndex = this.steps.findIndex((step) => step.dataset.key === 'size');
      if (sizeIndex !== -1) return sizeIndex;
    }
    return this.currentIndex + 1;
  }

  onBack() {
    if (this.history.length <= 1) return;
    this.history.pop();
    this.showStep(this.history[this.history.length - 1]);
  }

  onRetake() {
    this.answers = {};
    this.history = [0];
    this.form.reset();
    this.resultsPanel.hidden = true;
    this.form.hidden = false;
    this.progressWrap.hidden = false;
    this.showStep(0);
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showStep(index) {
    this.currentIndex = index;

    this.steps.forEach((step, i) => {
      step.hidden = i !== index;
    });

    this.backBtn.hidden = this.history.length <= 1;
    this.continueBtn.textContent = index === this.steps.length - 1 ? 'See My Results' : 'Continue';

    this.progressSegments.forEach((segment, i) => {
      segment.classList.toggle('is-filled', i <= index);
    });

    this.updateContinueState();
  }

  async showResults() {
    const { results, usedSizeFallback } = getRecommendations(this.products, this.answers);

    this.fallbackNote.hidden = !usedSizeFallback;
    this.resultsGrid.innerHTML = '';
    this.resultsGrid.classList.add('mattress-quiz__results-grid--loading');

    if (results.length === 0) {
      this.resultsGrid.classList.remove('mattress-quiz__results-grid--loading');
      const empty = document.createElement('p');
      empty.className = 'mattress-quiz__empty text-center';
      empty.textContent = "We couldn't find a match right now — please get in touch and we'll help you find the right mattress.";
      this.resultsGrid.appendChild(empty);
    } else {
      const cards = await Promise.all(results.map((product) => this.buildResultCard(product)));
      this.resultsGrid.classList.remove('mattress-quiz__results-grid--loading');
      cards.forEach((card) => this.resultsGrid.appendChild(card));
    }

    this.form.hidden = true;
    this.progressWrap.hidden = true;
    this.resultsPanel.hidden = false;
    this.resultsPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Fetches the real product-card markup via Shopify's `?sections=` AJAX API (same pattern
   * assets/compare.js already uses in this theme) so recommendations look identical to every
   * other product card on the site — same badges, price formatting, hover states — instead of a
   * hand-rolled lookalike. Falls back to a minimal plain-link card if the fetch fails.
   */
  async buildResultCard(product) {
    const sectionId = 'mattress-quiz-product-card';
    const wrapper = document.createElement('div');
    wrapper.className = 'mattress-quiz__result-card';

    try {
      const response = await fetch(`${product.url}?sections=${sectionId}`);
      const data = await response.json();
      const doc = new DOMParser().parseFromString(data[sectionId], 'text/html');
      const card = doc.querySelector('.product-card');
      if (!card) throw new Error('card markup not found');

      if (product.tier) {
        const imageWrapper = card.querySelector('.product-card__image-wrapper');
        if (imageWrapper) {
          const badgeWrap = document.createElement('div');
          badgeWrap.className = 'product-card__badge';
          const badge = document.createElement('span');
          badge.className = 'f-badge';
          badge.textContent = product.tier;
          badgeWrap.appendChild(badge);
          imageWrapper.prepend(badgeWrap);
        }
      }

      wrapper.appendChild(card);
    } catch (error) {
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.title;
      wrapper.appendChild(link);
    }

    return wrapper;
  }
}

customElements.define('mattress-quiz', MattressQuiz);
